import { differenceInDays } from 'date-fns/differenceInDays'
import { isAfter } from 'date-fns/isAfter'
import _get from 'lodash/get'
import { match } from 'ts-pattern'
import { z } from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import * as validators from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import { toLocalDate } from '@flows/insurance/utils/dateUtils'

const dental_coverage_dates = (params: StepConfigParams) =>
  ({
    schema: schemaUtils
      .generateZodObjectFromPaths({
        [params.helperFunctions.getParamPath('previous_insurance_start_date')]:
          validators.createPreprocessedDateSchema(z.date()),
        [params.helperFunctions.getParamPath('previous_insurance_end_date')]: validators
          .createPreprocessedDateSchema(z.date())
          .optional(),
        [params.helperFunctions.getParamPath('dental_coverage_previous_still_active')]: z
          .boolean()
          .optional(),
      })
      .superRefine((data, ctx) => {
        const stillActive = _get(
          data,
          params.helperFunctions.getParamPath('dental_coverage_previous_still_active')
        )
        const endDate = _get(
          data,
          params.helperFunctions.getParamPath('previous_insurance_end_date')
        )

        if (!stillActive && !endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('sharedFlows_common:generic.errors.required'),
            path: [params.helperFunctions.getParamPath('previous_insurance_end_date')],
          })
        }
      }),
    showOrderSummary: true,
    stepContent: [
      {
        type: 'customComponent',
        name: 'CoverageDateTitle',
        inputs: [],
        outputs: [],
      },
      {
        type: 'date',
        name: params.helperFunctions.getParamPath('previous_insurance_start_date'),
        noMargin: true,
        maxDate: 'now',
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.start_date.label',
        },
      },
      {
        type: 'date',
        name: params.helperFunctions.getParamPath('previous_insurance_end_date'),
        noMargin: true,
        disabledConditionConfig: {
          condition: {
            valuePath: params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
            expectedValue: true,
          },
          fallbackValue: '',
        },
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.end_date.label',
        },
      },
      {
        type: 'checkbox',
        name: params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
        noBackground: true,
        isOptional: true,
        fields: [
          {
            name: params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.stillowned.label',
            },
          },
        ],
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        noPadding: true,
        bgColor: 'grey200',
        contentConfiguration: [
          {
            type: 'title',
            size: 'h5',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_dental:steps.dental_coverage_dates.still_active_warning.title',
            },
          },
          {
            type: 'text',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_dental:steps.dental_coverage_dates.still_active_warning.text',
            },
          },
        ],
        condition: {
          type: 'value',
          valuePath: params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
          expectedValue: true,
        },
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        noPadding: true,
        bgColor: 'grey200',
        contentConfiguration: [
          {
            type: 'title',
            size: 'h5',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.limit_reset.title',
            },
          },
          {
            type: 'text',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.limit_reset.text',
            },
          },
        ],
        condition: {
          type: 'function',
          valuePaths: [
            'effective_on',
            params.helperFunctions.getParamPath('previous_insurance_end_date'),
            params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
          ],
          function: (values) => {
            const effectiveOn = toLocalDate(_get(values, 'effective_on'))
            const previousEndDate = toLocalDate(
              _get(values, params.helperFunctions.getParamPath('previous_insurance_end_date'))
            )

            const contractStillActive = _get(
              values,
              params.helperFunctions.getParamPath('dental_coverage_previous_still_active')
            )

            if (!effectiveOn || !previousEndDate || contractStillActive) {
              return false
            }

            const gapInDays = differenceInDays(effectiveOn, previousEndDate)

            return gapInDays > 1
          },
        },
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        noPadding: true,
        bgColor: 'grey200',
        contentConfiguration: [
          {
            type: 'title',
            size: 'h5',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_dental:steps.dental_coverage_dates.overlap_warning.title',
            },
          },
          {
            type: 'text',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_coverage_dates.overlap_warning.text',
            },
          },
        ],
        condition: {
          type: 'function',
          valuePaths: [
            'effective_on',
            params.helperFunctions.getParamPath('previous_insurance_end_date'),
            params.helperFunctions.getParamPath('dental_coverage_previous_still_active'),
          ],
          function: (values) => {
            const policyStartValue = toLocalDate(_get(values, 'effective_on'))
            const previousEndDate = toLocalDate(
              _get(values, params.helperFunctions.getParamPath('previous_insurance_end_date'))
            )

            const contractStillActive = _get(
              values,
              params.helperFunctions.getParamPath('dental_coverage_previous_still_active')
            )

            if (!policyStartValue || !previousEndDate || contractStillActive) {
              return false
            }

            return isAfter(previousEndDate, policyStartValue)
          },
        },
      },
    ],
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues) => {
        const hasActive = _get(
          flowValues,
          params.helperFunctions.getParamPath('previous_insurance_end_date')
        )

        if (hasActive) {
          return {
            formValues: {
              [params.helperFunctions.getParamPath('previous_insurance_end_date')]: undefined,
            },
          }
        }
      },
    },
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valueFunction: (values) => {
        const contractStillActive = _get(
          values,
          params.helperFunctions.getParamPath('dental_coverage_previous_still_active')
        )
        const effectiveOn = toLocalDate(_get(values, 'effective_on'))
        const previousEndDate = toLocalDate(
          _get(values, params.helperFunctions.getParamPath('previous_insurance_end_date'))
        )

        const gapInDays = differenceInDays(effectiveOn!, previousEndDate!)

        return match({
          contractStillActive,
          hasGap: gapInDays > 1,
        })
          .with({ contractStillActive: true }, () => 'dental_previous_coverage' as const)
          .with({ hasGap: true }, () => 'dental_previous_coverage_with_gap' as const)
          .otherwise(() => 'dental_previous_coverage' as const)
      },
      valueToStepName: {
        dental_previous_coverage: 'dental_previous_coverage',
        dental_previous_coverage_with_gap: 'dental_previous_coverage_with_gap',
      },
    },
  }) as const satisfies AnyStepConfiguration

export default dental_coverage_dates
